// frontend/src/pages/StartAudit.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { US_STATES as US_STATES_RAW } from "../components/universal-audit/constants";
import useEntitlement from "../hooks/useEntitlement";
import {
  readBusinessIndex as readSharedBusinessIndex,
  readBusinessPayload as readSharedBusinessPayload,
  getActiveBusinessId as getSharedActiveBusinessId,
  upsertTopTierBusiness,
  selectTopTierBusiness,
} from "../lib/businessStore";
import { getAuditSnapshotPayload, readAuditSnapshotIndex } from "../lib/auditHistory";

const STORAGE_KEYS = {
  USER: "aifine_user",
  ACTIVE_BUSINESS_ID: "aifine_active_business_id_v1",
  BUSINESS_INDEX: "aifine_business_index_v1",
  BUSINESS_PAYLOAD_PREFIX: "aifine_business_payload_",
  CONFIG: "aifine_config",
  ANSWERS_PREFIX: "aifine_answers_business_",
  PROGRESS_PREFIX: "aifine_assessment_progress_business_",
};

const STANDARD_SINGLE_ID = "single_default";

function safeParse(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function clamp(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
}

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function safeRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function readSubscriptionTierFromLocal() {
  const obj = safeParse(safeGetItem(STORAGE_KEYS.USER), null);
  const tier = obj?.subscription_tier ?? obj?.subscriptionTier ?? obj?.tier ?? null;
  return tier ? String(tier) : "single";
}

function isTopTierLocal(tier) {
  return String(tier || "").toLowerCase() === "top";
}

const SUPPORTED_CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar (USD)" },
  { code: "GBP", symbol: "£", label: "British Pound (GBP)" },
  { code: "EUR", symbol: "€", label: "Euro (EUR)" },
];

function getCurrencyMeta(code) {
  const normalized = String(code || "").trim().toUpperCase();
  return SUPPORTED_CURRENCIES.find((item) => item.code === normalized) || SUPPORTED_CURRENCIES[0];
}

function deriveCurrencyFromJurisdictions(selected) {
  const set = new Set(Array.isArray(selected) ? selected : []);
  if (set.has("EU")) return getCurrencyMeta("EUR");
  if (set.has("UK")) return getCurrencyMeta("GBP");
  return getCurrencyMeta("USD");
}

function formatMoney(n, symbol) {
  const num = Number(n);
  const safe = Number.isFinite(num) ? Math.round(num) : 0;
  return `${symbol}${safe.toLocaleString("en-US")}`;
}

function normalizeEntityName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function makeBusinessId() {
  return `biz_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function loadBusinessIndex() {
  const idx = safeParse(safeGetItem(STORAGE_KEYS.BUSINESS_INDEX), []);
  return Array.isArray(idx) ? idx : [];
}

function saveBusinessIndex(idx) {
  safeSetItem(STORAGE_KEYS.BUSINESS_INDEX, JSON.stringify(idx));
}

function saveBusinessPayload(businessId, payload) {
  safeSetItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`, JSON.stringify(payload));
}

function loadBusinessPayload(businessId) {
  return safeParse(safeGetItem(`${STORAGE_KEYS.BUSINESS_PAYLOAD_PREFIX}${businessId}`), null);
}

function setActiveBusinessIdStorage(businessId) {
  safeSetItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID, String(businessId));
}

function getActiveBusinessIdStorage() {
  return safeGetItem(STORAGE_KEYS.ACTIVE_BUSINESS_ID) || "";
}

function clearBusinessRunState(businessId) {
  safeRemoveItem(`${STORAGE_KEYS.ANSWERS_PREFIX}${businessId}`);
  safeRemoveItem(`${STORAGE_KEYS.PROGRESS_PREFIX}${businessId}`);
}

function revFromSlider(s, max) {
  const t = clamp(Number(s) || 0, 0, 1000) / 1000;
  if (t <= 0.55) {
    const u = t / 0.55;
    return Math.round(u * 10_000_000);
  }
  if (t <= 0.82) {
    const u = (t - 0.55) / (0.82 - 0.55);
    return Math.round(10_000_000 + u * (200_000_000 - 10_000_000));
  }
  const u = (t - 0.82) / (1 - 0.82);
  return Math.round(200_000_000 + Math.pow(u, 1.35) * (max - 200_000_000));
}

function sliderFromRev(v, max) {
  const val = clamp(Number(v) || 0, 0, max);
  if (val <= 10_000_000) {
    const u = 10_000_000 ? val / 10_000_000 : 0;
    return Math.round(u * 0.55 * 1000);
  }
  if (val <= 200_000_000) {
    const u = (val - 10_000_000) / (200_000_000 - 10_000_000);
    return Math.round((0.55 + u * (0.82 - 0.55)) * 1000);
  }
  const u = (val - 200_000_000) / (max - 200_000_000);
  const inv = Math.pow(clamp(u, 0, 1), 1 / 1.35);
  return Math.round((0.82 + inv * (1 - 0.82)) * 1000);
}

function peopleFromSlider(s, max) {
  const t = clamp(Number(s) || 0, 0, 1000) / 1000;
  const kneeT = 0.3;
  const kneeV = 0.05;
  if (t <= kneeT) {
    const u = t / kneeT;
    return Math.round(u * (max * kneeV));
  }
  const u = (t - kneeT) / (1 - kneeT);
  const gamma = 1.6;
  return Math.round(max * (kneeV + (1 - kneeV) * Math.pow(u, gamma)));
}

function sliderFromPeople(v, max) {
  const val = clamp(Number(v) || 0, 0, max);
  const kneeT = 0.3;
  const kneeV = 0.05;
  const kneeVal = max * kneeV;

  if (val <= kneeVal) {
    const u = kneeVal ? val / kneeVal : 0;
    return Math.round(u * kneeT * 1000);
  }
  const u = (val - kneeVal) / (max - kneeVal);
  const gamma = 1.6;
  const inv = Math.pow(clamp(u, 0, 1), 1 / gamma);
  return Math.round((kneeT + (1 - kneeT) * inv) * 1000);
}

export default function StartAudit() {
  const navigate = useNavigate();
  const location = useLocation();

  const localTier = useMemo(() => {
    try {
      return readSubscriptionTierFromLocal();
    } catch {
      return "single";
    }
  }, []);

  const ent = useEntitlement();
  const entitlementTier = ent?.tier ?? null;
  const entitlementIsTop = typeof ent?.isTopTier === "boolean" ? ent.isTopTier : null;

  const effectiveTier = useMemo(() => {
    return entitlementTier ? String(entitlementTier) : String(localTier || "single");
  }, [entitlementTier, localTier]);

  const top = useMemo(() => {
    if (typeof entitlementIsTop === "boolean") return entitlementIsTop;
    return isTopTierLocal(effectiveTier);
  }, [entitlementIsTop, effectiveTier]);

  const mode = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const m = String(params.get("mode") || "").toLowerCase();
    const requested = m === "new" ? "new" : "rerun";
    if (!top && requested === "new") return "rerun";
    return requested;
  }, [location.search, top]);

  const [businessIndex, setBusinessIndex] = useState([]);
  const [activeBusinessId, setActiveBusinessIdState] = useState("");
  const [selectedBusinessId, setSelectedBusinessId] = useState("");
  const [entityCreationMessage, setEntityCreationMessage] = useState("");
  const [entityCreationError, setEntityCreationError] = useState("");
  const [duplicateEntityCandidate, setDuplicateEntityCandidate] = useState(null);

  const [editingBusinessId, setEditingBusinessId] = useState("");
  const [clientName, setClientName] = useState("");
  const [industry, setIndustry] = useState("");
  const [niche, setNiche] = useState("");
  const [annualTurnover, setAnnualTurnover] = useState(0);
  const [userImpact, setUserImpact] = useState(0);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [selectedStates, setSelectedStates] = useState(new Set());
  const [defaultCurrencyCode, setDefaultCurrencyCode] = useState("USD");
  const entityNameInputRef = useRef(null);

  const US_STATES = safeArray(US_STATES_RAW);
  const REV_MAX = 5_000_000_000;
  const PEOPLE_MAX = 500_000;

  const derivedCurrency = useMemo(() => deriveCurrencyFromJurisdictions(jurisdictions), [jurisdictions]);
  const currency = useMemo(() => getCurrencyMeta(defaultCurrencyCode || derivedCurrency.code), [defaultCurrencyCode, derivedCurrency.code]);
  const shouldUseStates = useMemo(
    () => Array.isArray(jurisdictions) && jurisdictions.includes("US_STATE"),
    [jurisdictions]
  );
  const availableCurrencyCodes = useMemo(() => {
    const next = new Set();
    if (jurisdictions.includes("US_FED") || jurisdictions.includes("US_STATE")) next.add("USD");
    if (jurisdictions.includes("UK")) next.add("GBP");
    if (jurisdictions.includes("EU")) next.add("EUR");
    return Array.from(next);
  }, [jurisdictions]);
  const availableCurrencies = useMemo(
    () => availableCurrencyCodes.map((code) => getCurrencyMeta(code)),
    [availableCurrencyCodes]
  );
  const showCurrencySelector = availableCurrencies.length > 1;

  const [revSlider, setRevSlider] = useState(0);
  const [peopleSlider, setPeopleSlider] = useState(0);

  useEffect(() => {
    if (top) return;

    try {
      setActiveBusinessIdStorage(STANDARD_SINGLE_ID);
      setActiveBusinessIdState(STANDARD_SINGLE_ID);
      setEditingBusinessId(STANDARD_SINGLE_ID);

      const payload = loadBusinessPayload(STANDARD_SINGLE_ID);
      if (payload) {
        safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
      } else {
        const cfg = safeParse(safeGetItem(STORAGE_KEYS.CONFIG), null);
        if (cfg && typeof cfg === "object") {
          saveBusinessPayload(STANDARD_SINGLE_ID, cfg);
          safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(cfg));
        }
      }
    } catch {}
  }, [top]);

  useEffect(() => {
    const idx = readSharedBusinessIndex();
    setBusinessIndex(Array.isArray(idx) ? idx : []);

    const active = getSharedActiveBusinessId() || getActiveBusinessIdStorage();
    setActiveBusinessIdState(active || "");

    if (!active && idx.length) {
      const first = idx[0]?.business_id;
      if (first) {
        setActiveBusinessIdStorage(first);
        setActiveBusinessIdState(first);
      }
    }
  }, []);

  useEffect(() => {
    if (activeBusinessId) {
      setSelectedBusinessId(activeBusinessId);
      return;
    }
    if (businessIndex.length === 1) {
      const onlyId = businessIndex[0]?.business_id || "";
      setSelectedBusinessId(onlyId);
    }
  }, [activeBusinessId, businessIndex]);

  useEffect(() => {
    setEntityCreationMessage("");
    setEntityCreationError("");
    setDuplicateEntityCandidate(null);
  }, [mode, top]);

  useEffect(() => {
    if (!top) {
      const active = STANDARD_SINGLE_ID;
      setActiveBusinessIdStorage(active);
      setActiveBusinessIdState(active);

      const payload = loadBusinessPayload(active);
      if (payload) {
        setEditingBusinessId(active);
        setClientName(payload.clientName || "");
        setIndustry(payload.industry || "");
        setNiche(payload.niche || "");
        setAnnualTurnover(Number(payload.annualTurnover) || 0);
        setUserImpact(Number(payload.userImpact) || 0);
        setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
        setDefaultCurrencyCode(String(payload.currency_code || deriveCurrencyFromJurisdictions(payload.jurisdictions).code || "USD").toUpperCase());
        const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
        setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));
      } else {
        setEditingBusinessId(active);
        setClientName("");
        setIndustry("");
        setNiche("");
        setAnnualTurnover(0);
        setUserImpact(0);
        setJurisdictions([]);
        setDefaultCurrencyCode("USD");
        setSelectedStates(new Set());
      }
      return;
    }

    if (mode === "new") {
      setEditingBusinessId("");
      setClientName("");
      setIndustry("");
      setNiche("");
      setAnnualTurnover(0);
      setUserImpact(0);
      setJurisdictions([]);
      setDefaultCurrencyCode("USD");
      setSelectedStates(new Set());
      return;
    }

    const active = getActiveBusinessIdStorage();
    if (!active) return;
    const payload = loadBusinessPayload(active);
    if (!payload) return;

    setEditingBusinessId(active);
    setClientName(payload.clientName || "");
    setIndustry(payload.industry || "");
    setNiche(payload.niche || "");
    setAnnualTurnover(Number(payload.annualTurnover) || 0);
    setUserImpact(Number(payload.userImpact) || 0);
    setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
    setDefaultCurrencyCode(String(payload.currency_code || deriveCurrencyFromJurisdictions(payload.jurisdictions).code || "USD").toUpperCase());
    const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
    setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));
  }, [top, mode]);

  useEffect(() => {
    setRevSlider(sliderFromRev(annualTurnover, REV_MAX));
  }, [annualTurnover]);

  useEffect(() => {
    setPeopleSlider(sliderFromPeople(userImpact, PEOPLE_MAX));
  }, [userImpact]);

  useEffect(() => {
    setDefaultCurrencyCode((prev) => {
      const normalizedPrev = String(prev || "").trim().toUpperCase();
      const nextDerived = derivedCurrency.code;
      const isSupported = SUPPORTED_CURRENCIES.some((item) => item.code === normalizedPrev);
      if (!isSupported) return nextDerived;
      if (showCurrencySelector && !availableCurrencyCodes.includes(normalizedPrev)) {
        return nextDerived;
      }
      return normalizedPrev;
    });
  }, [derivedCurrency.code, showCurrencySelector, availableCurrencyCodes]);

  function toggleJur(code) {
    setJurisdictions((prev) => {
      const next = new Set(Array.isArray(prev) ? prev : []);
      next.has(code) ? next.delete(code) : next.add(code);
      if (!next.has("US_STATE")) setSelectedStates(new Set());
      return Array.from(next);
    });
  }

  function toggleState(code) {
    const c = String(code || "").toUpperCase();
    if (!c) return;
    setSelectedStates((prev) => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  }

  function clearStates() {
    setSelectedStates(new Set());
  }

  function selectAllStates() {
    setSelectedStates(new Set(US_STATES.map(([c]) => String(c).toUpperCase())));
  }

  const isValid = useMemo(() => {
    const baseOk =
      String(clientName || "").trim().length > 0 &&
      String(industry || "").trim().length > 0 &&
      Array.isArray(jurisdictions) &&
      jurisdictions.length > 0;

    if (!baseOk) return false;
    if (shouldUseStates) return (selectedStates?.size || 0) > 0;
    return true;
  }, [clientName, industry, jurisdictions, shouldUseStates, selectedStates]);

  function persistBusiness(businessId) {
    const payload = {
      business_id: businessId,
      clientName: String(clientName || "").trim(),
      industry: String(industry || "").trim(),
      niche: String(niche || "").trim(),
      annualTurnover: Number(annualTurnover) || 0,
      userImpact: Number(userImpact) || 0,
      jurisdictions: Array.isArray(jurisdictions) ? jurisdictions : [],
      selectedStates:
        Array.isArray(jurisdictions) && jurisdictions.includes("US_STATE") ? Array.from(selectedStates || []) : [],
      currency_code: currency.code,
      currency_symbol: currency.symbol,
      updated_at_iso: new Date().toISOString(),
    };

    saveBusinessPayload(businessId, payload);
    safeSetItem(STORAGE_KEYS.CONFIG, JSON.stringify(payload));
    return payload;
  }

  function upsertIndexEntry(businessId, payload) {
    const idx = loadBusinessIndex();
    const next = Array.isArray(idx) ? [...idx] : [];
    const i = next.findIndex((x) => x?.business_id === businessId);
    const entry = {
      business_id: businessId,
      entity_name: payload?.clientName || "Unnamed",
      jurisdictions: Array.isArray(payload?.jurisdictions) ? payload.jurisdictions : [],
      updated_at_iso: payload?.updated_at_iso || new Date().toISOString(),
    };
    if (i >= 0) next[i] = entry;
    else next.unshift(entry);

    saveBusinessIndex(next);
    setBusinessIndex(next);
  }

  function selectBusiness(businessId) {
    const payload = selectTopTierBusiness(businessId) || readSharedBusinessPayload(businessId) || loadBusinessPayload(businessId);
    if (!payload) return;

    setEntityCreationMessage("");
    setEntityCreationError("");
    setDuplicateEntityCandidate(null);

    setActiveBusinessIdStorage(businessId);
    setActiveBusinessIdState(businessId);
    setSelectedBusinessId(businessId);

    setEditingBusinessId(businessId);
    setClientName(payload.clientName || payload.entity_name || "");
    setIndustry(payload.industry || "");
    setNiche(payload.niche || "");
    setAnnualTurnover(Number(payload.annualTurnover) || 0);
    setUserImpact(Number(payload.userImpact) || 0);
    setJurisdictions(Array.isArray(payload.jurisdictions) ? payload.jurisdictions : []);
    setDefaultCurrencyCode(String(payload.currency_code || deriveCurrencyFromJurisdictions(payload.jurisdictions).code || "USD").toUpperCase());
    const st = Array.isArray(payload.selectedStates) ? payload.selectedStates : [];
    setSelectedStates(new Set(st.map((x) => String(x).toUpperCase())));

    navigate("/start-audit?mode=rerun");
  }

  function focusEntityNameInput() {
    try {
      entityNameInputRef.current?.focus();
      entityNameInputRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    } catch {}
  }

  function handleUseExistingEntity() {
    if (!duplicateEntityCandidate?.business_id) return;
    selectBusiness(duplicateEntityCandidate.business_id);
    setEntityCreationMessage(`Using existing entity: ${duplicateEntityCandidate.entity_name || "Selected Entity"}.`);
    setEntityCreationError("");
    setDuplicateEntityCandidate(null);
  }

  function createEntityShell(forceCreate = false) {
    if (!top) return;

    const trimmedName = String(clientName || "").trim();
    if (!trimmedName) {
      setEntityCreationError("Enter an entity name before creating the entity.");
      setEntityCreationMessage("");
      setDuplicateEntityCandidate(null);
      return;
    }

    const normalizedInput = normalizeEntityName(trimmedName);
    const matchingEntity = businessIndex.find(
      (row) => normalizeEntityName(row?.entity_name) === normalizedInput
    );

    if (matchingEntity && !forceCreate) {
      setDuplicateEntityCandidate(matchingEntity);
      setEntityCreationError("");
      setEntityCreationMessage(
        `A matching entity already exists: ${matchingEntity.entity_name || "Existing Entity"}.`
      );
      return;
    }

    const payload = {
      clientName: trimmedName,
      industry: "",
      niche: "",
      annualTurnover: 0,
      userImpact: 0,
      jurisdictions: [],
      selectedStates: [],
      currency_code: "USD",
      currency_symbol: "$",
      entity_shell: true,
      profile_complete: false,
      created_at_iso: new Date().toISOString(),
    };

    const result = upsertTopTierBusiness(payload);
    const createdId = result?.businessId || result?.payload?.business_id || "";
    const nextIndex = readSharedBusinessIndex();

    setBusinessIndex(Array.isArray(nextIndex) ? nextIndex : []);
    setActiveBusinessIdState(createdId);
    setSelectedBusinessId(createdId);
    setEditingBusinessId(createdId);
    setClientName(trimmedName);
    setIndustry("");
    setNiche("");
    setAnnualTurnover(0);
    setUserImpact(0);
    setJurisdictions([]);
    setDefaultCurrencyCode("USD");
    setSelectedStates(new Set());
    setDuplicateEntityCandidate(null);
    setEntityCreationError("");
    setEntityCreationMessage(
      forceCreate
        ? `New entity created: ${trimmedName}. You can now complete the profile and start the assessment when ready.`
        : `Entity created: ${trimmedName}. You can now complete the profile and start the assessment when ready.`
    );

    navigate("/start-audit?mode=rerun", { replace: true });
  }

  function startAssessment() {
    if (!isValid) return;

    let businessId = editingBusinessId;

    if (!top) {
      businessId = STANDARD_SINGLE_ID;
    } else if (top && mode === "new") {
      businessId = makeBusinessId();
    }

    if (!businessId) {
      businessId = getActiveBusinessIdStorage() || makeBusinessId();
    }

    let payload = persistBusiness(businessId);
    setActiveBusinessIdStorage(businessId);
    setActiveBusinessIdState(businessId);

    if (top) {
      const result = upsertTopTierBusiness(payload, businessId);
      payload = result?.payload || payload;
      setBusinessIndex(readSharedBusinessIndex());
      setSelectedBusinessId(businessId);
      setEntityCreationMessage("");
      setEntityCreationError("");
      setDuplicateEntityCandidate(null);
    } else {
      upsertIndexEntry(businessId, payload);
    }

    clearBusinessRunState(businessId);
    navigate("/assessment");
  }

  const activePayload = useMemo(() => {
    if (!activeBusinessId) return null;
    return readSharedBusinessPayload(activeBusinessId) || loadBusinessPayload(activeBusinessId);
  }, [activeBusinessId]);

  const activeLabel = useMemo(() => {
    return activePayload?.clientName ? String(activePayload.clientName) : "";
  }, [activePayload]);

  const isCreatingNewBusiness = top && mode === "new";
  const entityNameEditable = isCreatingNewBusiness || (!top && !activeLabel);
  const hasBusinessIndex = businessIndex.length > 0;

  function riskStatusFromGrade(grade, score) {
    const g = String(grade || "").trim().toUpperCase();
    if (g === "A") return { label: "Low Risk", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
    if (g === "B") return { label: "Guarded Risk", cls: "border-amber-200 bg-amber-50 text-amber-700" };
    if (g === "C") return { label: "Elevated Risk", cls: "border-orange-200 bg-orange-50 text-orange-700" };
    if (g === "D" || g === "E") return { label: "High Risk", cls: "border-rose-200 bg-rose-50 text-rose-700" };
    const n = Number(score);
    if (Number.isFinite(n)) {
      if (n <= 15) return { label: "Low Risk", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" };
      if (n <= 30) return { label: "Guarded Risk", cls: "border-amber-200 bg-amber-50 text-amber-700" };
      if (n <= 50) return { label: "Elevated Risk", cls: "border-orange-200 bg-orange-50 text-orange-700" };
      return { label: "High Risk", cls: "border-rose-200 bg-rose-50 text-rose-700" };
    }
    return { label: "Not Scored", cls: "border-slate-200 bg-slate-50 text-slate-500" };
  }

  function formatAuditDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  }

  const businessDisplayRows = useMemo(() => {
    const rows = (Array.isArray(businessIndex) ? businessIndex : []).map((row) => {
      const businessId = row?.business_id || "";
      let latest = null;
      let payload = null;
      try {
        latest = readAuditSnapshotIndex({ businessId })?.[0] || null;
        payload = latest?.snapshot_id ? getAuditSnapshotPayload(latest.snapshot_id) : null;
      } catch {
        latest = null;
        payload = null;
      }
      const assessment = payload?.assessment || {};
      const riskScore = Number.isFinite(Number(assessment?.risk_score_pct))
        ? Math.round(Number(assessment.risk_score_pct))
        : Number.isFinite(Number(assessment?.severity_score_0_100))
          ? Math.round(Number(assessment.severity_score_0_100))
          : Number.isFinite(Number(latest?.risk_score_pct))
            ? Math.round(Number(latest.risk_score_pct))
            : null;
      const riskGrade = assessment?.risk_grade || latest?.risk_grade || "";
      return { ...row, latest, riskScore, riskGrade, status: riskStatusFromGrade(riskGrade, riskScore) };
    });
    const current = rows.find((row) => row.business_id === activeBusinessId);
    return current ? [current, ...rows.filter((row) => row.business_id !== activeBusinessId)] : rows;
  }, [businessIndex, activeBusinessId]);

  const hasAnyCompletedAudit = useMemo(
    () => businessDisplayRows.some((row) => Boolean(row?.latest?.snapshot_id)),
    [businessDisplayRows],
  );

  const readInitialHelpOpenState = () => {
    try {
      return !safeArray(readSharedBusinessIndex()).some((row) => {
        const businessId = String(row?.business_id || "").trim();
        if (!businessId) return false;
        try {
          return readAuditSnapshotIndex({ businessId }).length > 0;
        } catch {
          return false;
        }
      });
    } catch {
      return true;
    }
  };

  const [gettingStartedOpen, setGettingStartedOpen] = useState(readInitialHelpOpenState);
  const [needHelpOpen, setNeedHelpOpen] = useState(readInitialHelpOpenState);

  useEffect(() => {
    const shouldOpen = !hasAnyCompletedAudit;
    setGettingStartedOpen(shouldOpen);
    setNeedHelpOpen(shouldOpen);
  }, [hasAnyCompletedAudit]);

  const jurisdictionOptions = [
    { code: "US_FED", label: "US (FED)", icon: "🇺🇸" },
    { code: "US_STATE", label: "US (STATE)", icon: "🇺🇸" },
    { code: "UK", label: "UK", icon: "🇬🇧" },
    { code: "EU", label: "EU", icon: "🇪🇺" },
  ];

  const revenueMarkers = [
    { label: `${currency.symbol}0`, value: 0 },
    { label: `${currency.symbol}50M`, value: 50_000_000 },
    { label: `${currency.symbol}250M`, value: 250_000_000 },
    { label: `${currency.symbol}1B`, value: 1_000_000_000 },
    { label: `${currency.symbol}1B+`, value: REV_MAX },
  ];

  const peopleMarkers = [
    { label: "0", value: 0 },
    { label: "5K", value: 5_000 },
    { label: "25K", value: 25_000 },
    { label: "100K", value: 100_000 },
    { label: "100K+", value: PEOPLE_MAX },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-8 py-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-950">Start New Audit</h1>
            <p className="mt-1 text-sm text-slate-600">Choose how you'd like to begin, then provide a few details about this audit.</p>
          </div>

          {top ? (
            <>
              <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <button type="button" onClick={() => navigate("/start-audit?mode=new")} className={cn("flex items-center justify-between rounded-2xl border p-5 text-left shadow-sm transition", mode === "new" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300")}>
                  <div className="flex items-center gap-4"><div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-2xl">🏢</div><div><div className="font-black text-slate-950">Create New Business</div><div className="mt-1 text-sm text-slate-600">Set up a new business and start a fresh audit.</div></div></div><span className="text-xl text-blue-600">›</span>
                </button>
                <button type="button" disabled={!hasBusinessIndex} onClick={() => navigate("/start-audit?mode=rerun")} className={cn("flex items-center justify-between rounded-2xl border p-5 text-left shadow-sm transition", mode === "rerun" ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300", !hasBusinessIndex ? "cursor-not-allowed opacity-50" : "")}>
                  <div className="flex items-center gap-4"><div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-2xl">📄</div><div><div className="font-black text-slate-950">Continue With Existing Business</div><div className="mt-1 text-sm text-slate-600">Use a business you've already added.</div></div></div><span className="text-xl text-blue-600">›</span>
                </button>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm"><button type="button" onClick={() => setGettingStartedOpen((v) => !v)} className="flex w-full items-center justify-between px-5 py-4 text-left"><div><div className="font-black text-slate-950">Getting Started</div><div className="mt-0.5 text-sm text-slate-600">Learn how to start an audit in a few simple steps.</div></div><span className="text-slate-600">{gettingStartedOpen ? "⌃" : "⌄"}</span></button>{gettingStartedOpen ? <div className="border-t border-slate-100 px-5 py-4 text-sm leading-6 text-slate-600">Choose or create the business, confirm the business profile and jurisdictions, then select <span className="font-bold text-slate-900">Save and Start Assessment</span> to begin Question 1.</div> : null}</div>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm"><button type="button" onClick={() => setNeedHelpOpen((v) => !v)} className="flex w-full items-center justify-between px-5 py-4 text-left"><div><div className="font-black text-slate-950">Need Help?</div><div className="mt-0.5 text-sm text-slate-600">Get support or view our help resources.</div></div><span className="text-slate-600">{needHelpOpen ? "⌃" : "⌄"}</span></button>{needHelpOpen ? <div className="border-t border-slate-100 px-5 py-4 text-sm leading-6 text-slate-600">Use the User Guide for step-by-step help. Choose the jurisdictions that apply to the business and use UNSURE during the assessment when a requirement cannot be verified.</div> : null}</div>
              </div>

              {hasBusinessIndex ? (
                <section className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="font-black text-slate-950">Choose which business you want to view.</div><div className="mt-1 text-sm text-slate-600">Your dashboard, reports, and remediation will update to reflect the selected business.</div>
                  <div className="mt-3 overflow-hidden rounded-xl border border-slate-200"><table className="min-w-full border-collapse text-sm"><thead className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500"><tr><th className="px-4 py-3 text-left">Business</th><th className="px-4 py-3 text-left">Jurisdiction</th><th className="px-4 py-3 text-left">Last Audit</th><th className="px-4 py-3 text-left">Protection Score</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Action</th></tr></thead><tbody>
                    {businessDisplayRows.map((row) => { const current = row.business_id === activeBusinessId; return <tr key={row.business_id} className={current ? "border-t border-slate-100 bg-emerald-50/70" : "border-t border-slate-100 bg-white"}><td className="px-4 py-3 font-bold text-slate-950">{row.entity_name || "Untitled Entity"}</td><td className="px-4 py-3 text-slate-700">{Array.isArray(row.jurisdictions) && row.jurisdictions.length ? row.jurisdictions.join(", ") : "—"}</td><td className="px-4 py-3 text-slate-700">{formatAuditDate(row.latest?.created_at_iso)}</td><td className="px-4 py-3 font-bold text-slate-950">{row.riskScore == null ? "—" : Math.round(clamp(100 - row.riskScore, 0, 100))}</td><td className="px-4 py-3"><span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${row.status.cls}`}>{row.status.label}</span></td><td className="px-4 py-3 text-right">{current ? <span className="inline-flex min-w-[82px] justify-center rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-700">Current</span> : <button type="button" onClick={() => selectBusiness(row.business_id)} className="inline-flex min-w-[82px] justify-center rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-blue-50 hover:text-blue-700">Select</button>}</td></tr>; })}
                  </tbody></table></div>
                </section>
              ) : null}
            </>
          ) : null}

          <section className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div><div className="font-black text-slate-950">Business Profile</div><div className="mt-1 text-sm text-slate-600">Tell us about the business you want to assess. This information helps us tailor the assessment to your organization.</div></div>

            <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
              <div><label className="text-sm font-black text-slate-900">Entity Name</label>{entityNameEditable ? <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Enter the new business name" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400" /> : <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700"><span>{clientName || activeLabel || "—"}</span><span>🔒</span></div>}{!entityNameEditable ? <div className="mt-1.5 text-xs text-slate-500">Entity name is set when the business is created and cannot be changed during the audit.</div> : null}</div>
              <div><label className="text-sm font-black text-slate-900">Jurisdiction</label><div className="mt-2 grid grid-cols-2 gap-3 xl:grid-cols-4">{jurisdictionOptions.map((item) => { const selected = jurisdictions.includes(item.code); return <button key={item.code} type="button" onClick={() => toggleJur(item.code)} className={cn("flex items-center justify-between rounded-xl border px-4 py-3 text-left transition", selected ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-blue-300")}><div className="flex items-center gap-3"><span className="text-2xl">{item.icon}</span><span className="text-sm font-black text-slate-950">{item.label}</span></div><span className={cn("flex h-5 w-5 items-center justify-center rounded border text-xs", selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-transparent")}>✓</span></button>; })}</div></div>
            </div>

            {shouldUseStates ? <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4"><div className="flex flex-wrap items-start justify-between gap-3"><div><div className="text-sm font-black text-slate-900">U.S. States</div><div className="mt-1 text-xs text-slate-500">Select the states where your AI impacts people.</div></div><div className="flex gap-2"><button type="button" onClick={selectAllStates} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold">Select All</button><button type="button" onClick={clearStates} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold">Clear</button></div></div><div className="mt-3 grid max-h-56 grid-cols-2 gap-2 overflow-auto md:grid-cols-4">{US_STATES.map(([code, name]) => { const c = String(code).toUpperCase(); const selected = selectedStates.has(c); return <button key={c} type="button" onClick={() => toggleState(c)} className={cn("rounded-xl border p-2.5 text-left", selected ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white")}><div className="text-xs font-black">{c}</div><div className="text-[11px] text-slate-600">{name}</div></button>; })}</div></div> : null}

            <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
              <div className="lg:col-span-2"><div className="font-black text-slate-950">Industry and Niche</div><div className="mt-1 text-sm text-slate-600">Select the industry and niche that best describe this business. This helps us tailor the assessment questions to your specific business risks.</div><div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2"><div><label className="text-sm font-black text-slate-900">Industry</label><input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Select an industry" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400" /></div><div><label className="text-sm font-black text-slate-900">Niche <span className="font-normal text-slate-500">(Optional)</span></label><input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="Select a niche (optional)" className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400" /></div></div></div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4"><div className="font-black text-slate-900">Why we ask for industry and niche?</div><div className="mt-1 text-sm leading-5 text-slate-600">Different industries face different AI risks and regulatory requirements. This helps us provide more relevant and accurate assessment questions and guidance for your business.</div></div>
            </div>

            <div className="mt-5"><div className="text-sm font-black text-slate-900">Annual Revenue <span className="ml-1 text-blue-600">ⓘ</span></div><div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500"><div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white"><span className="border-r border-slate-200 px-3 py-2 font-bold text-slate-700">{currency.symbol}</span><input inputMode="numeric" value={Number(annualTurnover || 0).toLocaleString("en-US")} onChange={(e) => { const raw = String(e.target.value || "").replace(/[^0-9]/g, ""); const n = Math.min(REV_MAX, Number(raw || 0)); setAnnualTurnover(n); setRevSlider(sliderFromRev(n, REV_MAX)); }} className="w-40 px-3 py-2 text-sm font-semibold text-slate-900 outline-none" /></div><span>Enter a value or use the slider. Both are synchronized.</span></div><input type="range" min={0} max={1000} value={revSlider} onChange={(e) => { const value = clamp(e.target.value, 0, 1000); setRevSlider(value); setAnnualTurnover(revFromSlider(value, REV_MAX)); }} className="mt-2 w-full" /><div className="relative mt-1 h-5 text-xs text-slate-500">{revenueMarkers.map((m) => <span key={m.label} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${sliderFromRev(m.value, REV_MAX) / 10}%` }}>{m.label}</span>)}</div></div>

            <div className="mt-3"><div className="text-sm font-black text-slate-900">People Impacted <span className="font-normal text-slate-600">(Customers, Employees, or Both)</span> <span className="ml-1 text-blue-600">ⓘ</span></div><div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500"><input inputMode="numeric" value={Number(userImpact || 0).toLocaleString("en-US")} onChange={(e) => { const raw = String(e.target.value || "").replace(/[^0-9]/g, ""); const n = Math.min(PEOPLE_MAX, Number(raw || 0)); setUserImpact(n); setPeopleSlider(sliderFromPeople(n, PEOPLE_MAX)); }} className="w-48 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none" /><span>Enter a value or use the slider. Both are synchronized.</span></div><input type="range" min={0} max={1000} value={peopleSlider} onChange={(e) => { const value = clamp(e.target.value, 0, 1000); setPeopleSlider(value); setUserImpact(peopleFromSlider(value, PEOPLE_MAX)); }} className="mt-2 w-full" /><div className="relative mt-1 h-5 text-xs text-slate-500">{peopleMarkers.map((m) => <span key={m.label} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${sliderFromPeople(m.value, PEOPLE_MAX) / 10}%` }}>{m.label}</span>)}</div></div>

            <div className="mt-4 flex items-center justify-end gap-3"><button type="button" onClick={() => navigate("/dashboard")} className="rounded-xl border border-blue-300 bg-white px-5 py-2.5 text-sm font-black text-blue-700 hover:bg-blue-50">Cancel</button><button type="button" disabled={!isValid} onClick={startAssessment} className={cn("rounded-xl px-5 py-2.5 text-sm font-black", isValid ? "bg-blue-600 text-white hover:bg-blue-700" : "cursor-not-allowed bg-slate-200 text-slate-500")}>Save and Start Assessment →</button></div>
            {!isValid ? <div className="mt-2 text-right text-xs text-slate-500">{!jurisdictions.length ? "Select at least one jurisdiction to continue." : shouldUseStates && selectedStates.size === 0 ? "US (STATE) selected — please choose at least one state." : "Complete the required business profile fields to continue."}</div> : null}
          </section>

          <div className="mt-8 pb-6 text-xs text-slate-400">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</div>
        </div>
      </main>
    </div>
  );
}
